import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  emptyCellOneLevel,
  formatDateWithTime,
  formatQuantity,
  hasAllPermissions,
  hasPermission,
} from '@/shared';
import {
  useFetchCitiesEnabled,
  useUpdateCity,
} from '@/shared/actions/erp/cities.actions';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import {
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from '@/shared/hooks';
import { City } from '@/shared/interfaces/erp/system-settings';
import { useUiConfirmModalStore } from '@/store/ui';

export type CitiesPageProps = {};

const returnUrlCitiesPage = ROUTER_PATHS.administration.ciudadesNav;

const CitiesPage: React.FC<CitiesPageProps> = () => {
  useCheckPermissions(PermissionsEnum.api_view_ciudad);

  const navigate = useNavigate();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex } = pagination;

  ///* fetch data
  const {
    data: citiesPagingRes,
    isLoading,
    isRefetching,
  } = useFetchCitiesEnabled(
    {
      params: {
        page: pageIndex + 1,
        nombre: searchTerm,
        ...filterObject,
        filterByState: false,
      }
    }
  );

  ///* mutations
  const updateState = useUpdateCity({});

  ///* columns
  const columns = useMemo<MRT_ColumnDef<City>[]>(
    () => [
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },
    /*   {
        accessorKey: 'codigo',
        header: 'Código',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'codigo'),
      }, */
      {
        accessorKey: 'metraje_autorizado',
        header: 'Metraje Autorizado',
        size: 200,
        Cell: ({ row }) =>
          row.original?.metraje_autorizado
            ? `${formatQuantity(row.original.metraje_autorizado)} m`
            : 'N/A,',
      },

      ///* fk
      {
        accessorKey: 'pais',
        header: 'País',
        size: 200,
        Cell: ({ row }) => row.original?.pais_data?.nombre || 'N/A',
      },
      {
        accessorKey: 'provincia',
        header: 'Provicia',
        size: 200,
        Cell: ({ row }) => row.original?.provincia_data?.nombre || 'N/A',
      },

      {
        accessorKey: 'cobertura',
        header: 'Cobertura',
        size: 200,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original?.cobertura}
              titulo={row.original.cobertura ? 'SI' : 'NO'}
              setEstadoSwitch={value => {
                if (!hasPermission(PermissionsEnum.api_change_ciudad)) return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado Cobertura',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updateState.mutate({
                      id: row.original?.id_ciudad!,
                      data: { cobertura: !!value } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },

      ///
      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original.estado}
              titulo={row.original.estado ? 'Activo' : 'Inactivo'}
              setEstadoSwitch={provincia => {
                if (!hasPermission(PermissionsEnum.api_change_ciudad)) return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updateState.mutate({
                      id: row.original?.id_ciudad!,
                      data: { estado: !!provincia } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original?.fecha_creacion
            ? formatDateWithTime(row.original.fecha_creacion)
            : 'N/A',
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'Fecha de Modificación',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original?.fecha_modificacion
            ? formatDateWithTime(row.original.fecha_modificacion)
            : 'N/A',
      },
    ],
    []
  );

  ///* handlers
  const onEdit = (original: City) => {
    navigate(`${returnUrlCitiesPage}/editar/${original.id_ciudad!}`);
  };



  return (
    <SingleTableBoxScene
      title="Ciudades"
      urlRedirect={`${returnUrlCitiesPage}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_ciudad)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="por nombre"
      />

      <GeneralTable<City>
        columns={columns}
        data={citiesPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={citiesPagingRes?.data?.count}
        // // actions ========
        enableColumnActions={hasAllPermissions([
          PermissionsEnum.api_change_ciudad,
          PermissionsEnum.api_view_pais,
          PermissionsEnum.api_view_provincia,
        ])}
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        // crud
        canEdit={hasAllPermissions([
          PermissionsEnum.api_change_ciudad,
          PermissionsEnum.api_view_pais,
          PermissionsEnum.api_view_provincia,
        ])}
        onEdit={onEdit}
        canDelete={false}
      // onDelete={onDelete}
      />
    </SingleTableBoxScene>
  );
};

export default CitiesPage;
